import { useState } from 'react';
import { SelectFilters, SelectOption } from '../searchableSelect/searchableSelectTypes';
import { formatMatterReference } from '../../constants/matter';
import { useUser } from '../../context/UserContext';
import { useWorkTime } from '../../context/WorkTimeContext';
import { useActivityData } from '../../hooks/useActivityData';
import { getAppliedFilterState } from '../../helpers/appliedFilterHelper';
import { ITask } from '../../interface/tasks';
import { IWorkTime } from '../../interface/workTimes';
import { useActivityFilter } from './useActivityFilter';

const toActivityOption = (activity: ITask): SelectOption => {
  const matterReference = formatMatterReference(activity.matter);
  return {
    value: activity.id,
    label: activity.subject ?? '',
    ...(matterReference && { description: matterReference }),
  };
};

/**
 * The props `SearchableSelect` needs to pick a work time's activity. Like the picker screen, it
 * lists the activities of the work time's matter when there is one, else all of them, and the
 * filter the user picks there or here applies to both.
 */
export const useWorkTimeActivitySelect = () => {
  const { workTime, updateWorkTime } = useWorkTime();
  const { user } = useUser();
  const [searchValue, setSearchValue] = useState('');
  const { selectedFilter, clearFilter } = useActivityFilter();
  const {
    data: activities,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useActivityData({
    selectedFilter,
    searchValue,
    linkedMatter: workTime?.matter,
    user,
  });

  const { appliedFilters } = getAppliedFilterState({
    selectedFilter: [selectedFilter],
    checkboxOptions: [],
    disableFilter: false,
  });
  const filters: SelectFilters = {
    applied: appliedFilters,
    onRemove: clearFilter,
    onClearAll: clearFilter,
  };

  const options: SelectOption[] = activities.map(toActivityOption);
  const value: SelectOption | null = workTime?.activity?.id
    ? toActivityOption(workTime.activity)
    : null;

  const onChange = (option: SelectOption) => {
    const activity = activities.find(({ id }: ITask) => id === option.value);
    if (!activity) return;
    updateWorkTime({
      ...workTime,
      activity,
      activityId: activity.id,
      ...(activity.matter && { matter: activity.matter, matterId: activity.matter.id }),
    } as IWorkTime);
  };

  return {
    options,
    value,
    filters,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
